import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate, fmtISODate } from "@/lib/dates";
import { Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Complaints() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ service_user_id: "", complainant_name: "", complainant_relationship: "", date_received: fmtISODate(new Date()), how_received: "telephone", nature: "", nature_category: "Communication", seriousness: "low" });
  const { user } = useAuth();

  const load = () => api.get("/complaints").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); api.get("/service-users").then(({ data }) => setUsers(data.items || [])).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/complaints", { ...form, date_received: new Date(form.date_received).toISOString() });
    setCreating(false);
    setForm({ ...form, complainant_name: "", complainant_relationship: "", nature: "" });
    load();
  };

  const isMgmt = ["registered_manager", "compliance_manager", "nominated_individual", "super_admin"].includes(user.role);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{user.role === "family_member" ? "Concerns / complaints" : "Complaints"}</h1>
          <p className="text-sm text-slate-600">CQC Regulation 16 — receiving and acting on complaints.</p>
        </div>
        <button onClick={() => setCreating((v) => !v)} data-testid="new-complaint-button" className="inline-flex items-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium min-h-[44px]">
          <Plus className="h-4 w-4" /> Log complaint
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="complaint-form">
          <select value={form.service_user_id} onChange={(e) => setForm({ ...form, service_user_id: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md">
            <option value="">Service user (optional)…</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
          <input required placeholder="Complainant name" value={form.complainant_name} onChange={(e) => setForm({ ...form, complainant_name: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" />
          <input required placeholder="Relationship" value={form.complainant_relationship} onChange={(e) => setForm({ ...form, complainant_relationship: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" />
          <input type="date" value={form.date_received} onChange={(e) => setForm({ ...form, date_received: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" />
          <select value={form.how_received} onChange={(e) => setForm({ ...form, how_received: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md">
            {["telephone", "verbal", "email", "written", "via_portal"].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select value={form.nature_category} onChange={(e) => setForm({ ...form, nature_category: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md">
            {["Care Quality", "Staff Conduct", "Communication", "Administration", "Safeguarding", "Other"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <select value={form.seriousness} onChange={(e) => setForm({ ...form, seriousness: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" data-testid="complaint-seriousness">
            <option value="low">Low (28 day response)</option>
            <option value="medium">Medium (20 day response)</option>
            <option value="high">High (10 day response)</option>
          </select>
          <textarea required placeholder="Nature of complaint" rows={4} value={form.nature} onChange={(e) => setForm({ ...form, nature: e.target.value })} className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-md" />
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 border border-slate-300 rounded-md">Cancel</button>
            <button type="submit" data-testid="complaint-submit-button" className="px-4 py-2 bg-[#0D7377] text-white rounded-md font-medium">Submit</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
              <th className="text-left py-2 px-3">Received</th>
              <th className="text-left py-2 px-3">Complainant</th>
              <th className="text-left py-2 px-3">Nature</th>
              <th className="text-left py-2 px-3">Seriousness</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-left py-2 px-3">Response due</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => {
              const due = c.response_due_date ? new Date(c.response_due_date) : null;
              const overdue = due && c.status !== "closed" && due < new Date();
              return (
                <tr key={c.id} className="border-b border-slate-100">
                  <td className="py-3 px-3 text-slate-700">{fmtDate(c.date_received)}</td>
                  <td className="py-3 px-3 text-slate-800">{c.complainant_name} <span className="text-xs text-slate-500">({c.complainant_relationship})</span></td>
                  <td className="py-3 px-3"><p className="text-slate-900">{c.nature_category}</p><p className="text-xs text-slate-500 line-clamp-1">{c.nature}</p></td>
                  <td className="py-3 px-3"><StatusBadge status={c.seriousness} /></td>
                  <td className="py-3 px-3"><StatusBadge status={c.status || "open"} /></td>
                  <td className="py-3 px-3"><StatusBadge status={overdue ? "overdue" : "active"} label={overdue ? "Overdue" : (due || "").toString().slice(4, 10)} /></td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-500">No complaints.</td></tr>}
          </tbody>
        </table>
      </div>
      {!isMgmt && <p className="text-xs text-slate-500">Management triages and responds within statutory timescales.</p>}
    </div>
  );
}
